import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import type EmberRouter from '@ember/routing/router';
import type PublicTransition from '@ember/routing/transition';
import EmberService, { service } from '@ember/service';

import type { ArtifactLeaveGuard } from '@databox/types/domains/ai/chat';

import type { Service } from 'webapp-types';

// Holds Ember route transitions while a React island has work that leaving would drop (today the
// Genie artifact edit mode: unsent comments, unsaved text edits). Each island registers its guard
// through the `registerLeaveGuard` prop only while that work exists and the island is on screen, and
// owns the dialog; this service is the one place every Ember transition consults them (sidebar and
// main menu via the navigation island's onNavigate, in-app links, browser Back/Forward).
export default class LeaveGuardService extends EmberService {
	@service('router') declare routerService: Service.Router;

	// Transitions started through `withoutGuard` (and the redirects they cause) pass until they
	// settle. A counter, because a newer exempt transition can abort an older one mid-flight.
	private exemptTransitions = 0;
	private boundRouter?: EmberRouter;
	// One per island (the Genie page and the side panel can both hold work).
	private guards = new Set<ArtifactLeaveGuard>();

	// Passed to the islands as `registerLeaveGuard`; the returned function unregisters.
	@action
	register(guard: ArtifactLeaveGuard): () => void {
		this.guards.add(guard);

		return () => {
			this.guards.delete(guard);
		};
	}

	// Listens on the EmberRouter, not the router service. Ember dispatches an event's listeners in
	// reverse registration order, and the EmberRouter triggers `routeWillChange` before the router
	// service does, so this is the only place that runs ahead of every router-service listener
	// whatever order they were bound in. `abort()` marks the same transition object, which those
	// listeners (navigation closing the side panel, the activated route) then see as `isAborted`.
	// The public router service has no way to run first, hence the private lookup.
	bindRouterEvents(): void {
		// eslint-disable-next-line ember/no-private-routing-service
		this.boundRouter = getOwner(this)!.lookup('router:main') as EmberRouter;
		this.boundRouter.on('routeWillChange', this.handleRouteWillChange);
	}

	// For transitions the guard must not hold: the retry after Discard, and the Genie URL sync, which
	// only mirrors a panel switch React has already guarded itself.
	withoutGuard(startTransition: () => PublicTransition): void {
		const release = (): void => {
			this.exemptTransitions -= 1;
		};

		this.exemptTransitions += 1;

		let transition: PublicTransition | undefined;
		try {
			transition = startTransition();
		} finally {
			if (transition) {
				// Settles on success, redirect and abort alike (RSVP ignores TransitionAborted).
				void transition.finally(release);
			} else {
				release();
			}
		}
	}

	@action
	handleRouteWillChange(transition: PublicTransition): void {
		if (
			this.exemptTransitions > 0 ||
			// abort() announces itself as an aborted transition, and an earlier route hook may have
			// aborted this one already
			transition.isAborted ||
			// loading/error substates
			transition.isIntermediate ||
			// the same route with only query params changed is not a navigation away
			transition.queryParamsOnly ||
			!transition.from
		) {
			return;
		}

		const guard = [...this.guards].find((candidate) => candidate.isBlocking());

		if (!guard) {
			return;
		}

		const { location, currentURL } = this.routerService;
		const historyLocation = typeof location === 'string' ? undefined : location;
		// Back/Forward (router.handleURL) start after popstate already moved the address bar, while
		// the router still reports the page being left. `transition.urlMethod` can't tell: Ember sets
		// it to null only after this event. Link clicks and transitionTo haven't touched the URL yet.
		const isUrlTransition = Boolean(
			historyLocation && currentURL && historyLocation.getURL() !== currentURL,
		);

		transition.abort();

		if (isUrlTransition) {
			// Show the page we stay on in the history entry the browser moved to.
			historyLocation!.replaceURL?.(currentURL!);
		}

		guard.request(() => {
			this.withoutGuard(() => {
				const retried = transition.retry();

				// The retry of a URL transition would push a new entry; put the target URL back
				// into the entry Back/Forward moved to instead.
				if (isUrlTransition) {
					retried.method('replace');
				}

				return retried;
			});
		});
	}

	override willDestroy(): void {
		super.willDestroy();

		// ai-chat looks the service up for withoutGuard too, so it can exist unbound (unit tests).
		this.boundRouter?.off('routeWillChange', this.handleRouteWillChange);
	}
}

declare module '@ember/service' {
	interface Registry {
		'leave-guard': LeaveGuardService;
	}
}
